<script lang="ts">
	import { Tabs } from '@skeletonlabs/skeleton-svelte';
	import type { StatsPeriod } from '$entities/stats';
	import { PERIOD_OPTIONS, formatDateRange } from '$lib/shared/lib/statsFormatters';

	interface Props {
		period: StatsPeriod;
		startDate?: string;
		endDate?: string;
		onperiodchange?: (period: StatsPeriod) => void;
		oncustomrangeapply?: (detail: { startDate: string; endDate: string }) => void;
		oncustomrangeclear?: () => void;
	}

	let {
		period,
		startDate = '',
		endDate = '',
		onperiodchange,
		oncustomrangeapply,
		oncustomrangeclear
	}: Props = $props();

	// Local state for date inputs
	let localStartDate = $state(startDate);
	let localEndDate = $state(endDate);
	let showDatePicker = $state(!!(startDate && endDate));

	// Determine active period (custom if dates are set)
	let activePeriod = $derived(startDate && endDate ? 'custom' : period);

	// Sync local state when props change (DOM-coupled side effect — must be $effect, not $derived).
	$effect(() => {
		localStartDate = startDate;
		localEndDate = endDate;
		showDatePicker = !!(startDate && endDate);
	});

	function handlePeriodClick(newPeriod: StatsPeriod) {
		if (newPeriod === 'custom') {
			showDatePicker = true;
			return;
		}
		showDatePicker = false;
		onperiodchange?.(newPeriod);
	}

	function applyCustomRange() {
		if (!localStartDate || !localEndDate) return;
		oncustomrangeapply?.({ startDate: localStartDate, endDate: localEndDate });
	}

	function clearCustomRange() {
		showDatePicker = false;
		localStartDate = '';
		localEndDate = '';
		oncustomrangeclear?.();
	}
</script>

<Tabs value={activePeriod} onValueChange={(d) => handlePeriodClick(d.value as StatsPeriod)}>
	<Tabs.List>
		{#each PERIOD_OPTIONS as opt (opt.value)}
			<Tabs.Trigger value={opt.value}>{opt.label}</Tabs.Trigger>
		{/each}
	</Tabs.List>
</Tabs>

{#if showDatePicker}
	<div class="card p-4">
		<div class="flex flex-wrap items-end gap-4">
			<div class="flex flex-col gap-1">
				<label for="period-start-date" class="text-sm text-surface-700-300">Start Date</label>
				<input
					id="period-start-date"
					type="date"
					bind:value={localStartDate}
					class="input px-3 py-2"
				/>
			</div>
			<div class="flex flex-col gap-1">
				<label for="period-end-date" class="text-sm text-surface-700-300">End Date</label>
				<input id="period-end-date" type="date" bind:value={localEndDate} class="input px-3 py-2" />
			</div>
			<button
				class="btn preset-filled-primary-500"
				onclick={applyCustomRange}
				disabled={!localStartDate || !localEndDate}
			>
				Apply
			</button>
			<button class="btn border border-surface-500 preset-tonal" onclick={clearCustomRange}>
				Clear
			</button>
		</div>
		{#if startDate && endDate}
			<p class="mt-2 text-sm text-surface-700-300">
				Showing data for: <span class="font-medium">{formatDateRange(startDate, endDate)}</span>
			</p>
		{/if}
	</div>
{/if}
